import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import {
    Bucket, KeySelection, RunRow, WindowKey, executionStatsService, isAbort
} from '../../services/executionStatsService';
import { attentionInk, relativeTime } from './executionStatsVocabulary';
import { WINDOW_MS, windowLabel } from './executionStatsWindows';
import { dsVar, ink } from '../../theme/tokens';

/** The buckets a success can recover from. Pending review is waiting, not broken. */
export type ProblemBucket = Extract<Bucket, 'failed' | 'stalled'>;

const PROBLEM_NOUN: Record<ProblemBucket, string> = { failed: 'failure', stalled: 'stalled run' };

interface RunRecoveryProps {
    selection: KeySelection & { key: string };
    window: WindowKey;
    /** The problem buckets with runs in the window. Nothing is fetched without one. */
    problems: ProblemBucket[];
}

interface Latest {
    problem: { bucket: ProblemBucket; at: number } | null;
    successAt: number | null;
}

const startedAt = (run?: RunRow): number | null => {
    const at = run?.startedAt ? Date.parse(run.startedAt) : NaN;
    return Number.isFinite(at) ? at : null;
};

/**
 * Whether a workflow with problems has run cleanly since.
 *
 * The counts say something failed this month; this says whether it is still
 * failing, which decides whether anyone needs to act. One newest-run request
 * per bucket involved, each a single row.
 */
const RunRecovery: React.FC<RunRecoveryProps> = ({ selection, window, problems }) => {
    const [latest, setLatest] = useState<Latest | null>(null);
    const { scope, keyType, key, groupVersions } = selection;
    const problemKey = problems.join(',');

    useEffect(() => {
        setLatest(null);
        if (!problemKey) return;
        const controller = new AbortController();
        const to = Date.now();
        const range = { from: new Date(to - WINDOW_MS[window]).toISOString(), to: new Date(to).toISOString() };
        const newest = (bucket: Bucket) => executionStatsService
            .listRuns(
                { scope, keyType, keys: [key], groupVersions, ...range, bucket, page: 1, limit: 1 },
                { signal: controller.signal }
            )
            .then((res) => startedAt(res.runs[0]));

        const buckets = problemKey.split(',') as ProblemBucket[];
        Promise.all([newest('success'), ...buckets.map(newest)])
            .then(([successAt, ...problemTimes]) => {
                const problem = buckets
                    .map((bucket, i) => ({ bucket, at: problemTimes[i] }))
                    .filter((p): p is { bucket: ProblemBucket; at: number } => p.at !== null)
                    .sort((a, b) => b.at - a.at)[0] ?? null;
                setLatest({ problem, successAt });
            })
            // A line of context, not the panel's content: on failure it is left out.
            .catch((err) => { if (!isAbort(err)) setLatest(null); });
        return () => controller.abort();
    }, [scope, keyType, key, groupVersions, window, problemKey]);

    if (!latest?.problem) return null;
    const { problem, successAt } = latest;
    const noun = PROBLEM_NOUN[problem.bucket];
    const iso = (at: number) => new Date(at).toISOString();

    if (successAt !== null && successAt > problem.at) {
        return (
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft') }}>
                <strong>Recovered</strong> · last succeeded {relativeTime(iso(successAt))}, after the latest {noun}{' '}
                {relativeTime(iso(problem.at))}.
            </Typography>
        );
    }
    return (
        <Typography sx={{ fontSize: '0.75rem', color: ink(attentionInk(problem.bucket)) }}>
            <strong>Not recovered</strong> · no success since the latest {noun} {relativeTime(iso(problem.at))}
            {successAt === null
                ? `, and none in the ${windowLabel(window)}.`
                : ` · last succeeded ${relativeTime(iso(successAt))}.`}
        </Typography>
    );
};

export default RunRecovery;
